import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

const LABEL_STYLES = {
  pancham: 'bg-teal-500/20 text-teal-300 border-teal-500/40',
  atham: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
  chaudas: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
};
const CYCLE = ['pancham', 'atham', 'chaudas', 'none', null];

export default function JainCalendarSection({ api }) {
  const [settings, setSettings] = useState(null);
  const [month, setMonth] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() + 1 }; });
  const [days, setDays] = useState([]);

  const loadMonth = useCallback(async (y, m) => {
    try {
      const r = await api.get(`/jain/month?year=${y}&month=${m}`);
      setDays(r.data.days || []);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => {
    api.get('/jain/settings').then(r => setSettings(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => { loadMonth(month.y, month.m); }, [month, loadMonth]);

  const saveSettings = async (next) => {
    setSettings(next);
    try {
      await api.put('/jain/settings', next);
      toast.success('Jain calendar settings saved');
      loadMonth(month.y, month.m);
    } catch (e) { toast.error('Could not save'); }
  };

  const cycleDay = async (day) => {
    let cur;
    if (day.overridden) cur = day.label || 'none';
    else cur = null;
    const idx = CYCLE.indexOf(cur);
    const next = CYCLE[(idx + 1) % CYCLE.length];
    try {
      const r = await api.put('/jain/override', { date: day.date, label: next });
      setDays(prev => prev.map(d => d.date === day.date ? { ...d, ...r.data } : d));
    } catch (e) { toast.error('Could not update day'); }
  };

  if (!settings) return null;
  const nav = (dir) => setMonth(({ y, m }) => {
    const nm = m + dir;
    const next = nm < 1 ? { y: y - 1, m: 12 } : nm > 12 ? { y: y + 1, m: 1 } : { y, m: nm };
    return (next.y < 2020 || next.y > 2035) ? { y, m } : next;
  });
  const firstDow = (new Date(month.y, month.m - 1, 1).getDay() + 6) % 7; // Mon=0
  const monthName = new Date(month.y, month.m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-4" data-testid="jain-calendar-section">
      <div className="rounded-2xl p-4 space-y-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <p className="text-xs font-bold text-white">🙏 Tithi days to follow (no-vegetable cooking)</p>
        <p className="text-[11px] text-neutral-500">On followed days the AI Chef plans only dal/besan, dairy and grain-based food — no vegetables. Chaudas is on by default.</p>
        <div className="flex flex-wrap gap-3">
          {['chaudas', 'atham', 'pancham'].map(t => (
            <label key={t} className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-xl bg-white/5 border border-white/10">
              <input type="checkbox" checked={!!settings.follow[t]} data-testid={`jain-follow-${t}`}
                onChange={e => saveSettings({ ...settings, follow: { ...settings.follow, [t]: e.target.checked } })}
                className="accent-amber-500" />
              <span className="text-xs font-semibold text-neutral-200 capitalize">{t}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-2xl p-4 space-y-3" style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.2)' }}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!settings.chaturmas.enabled} data-testid="chaturmas-enabled"
            onChange={e => saveSettings({ ...settings, chaturmas: { ...settings.chaturmas, enabled: e.target.checked } })}
            className="accent-emerald-500" />
          <span className="text-xs font-bold text-emerald-300">Chaturmas period (all meals strictly Jain — no root vegetables)</span>
        </label>
        {settings.chaturmas.enabled && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-[11px] text-neutral-400">Start
              <input type="date" value={settings.chaturmas.start} data-testid="chaturmas-start"
                onChange={e => saveSettings({ ...settings, chaturmas: { ...settings.chaturmas, start: e.target.value } })}
                className="ml-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" />
            </label>
            <label className="text-[11px] text-neutral-400">End (Samvatsari / Kartik Sud 11)
              <input type="date" value={settings.chaturmas.end} data-testid="chaturmas-end"
                onChange={e => saveSettings({ ...settings, chaturmas: { ...settings.chaturmas, end: e.target.value } })}
                className="ml-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" />
            </label>
          </div>
        )}
      </div>

      <div className="rounded-2xl p-4 space-y-3" style={{ background: 'rgba(244,63,94,0.04)', border: '1px solid rgba(244,63,94,0.2)' }}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!settings.paryushan?.enabled} data-testid="paryushan-enabled"
            onChange={e => saveSettings({ ...settings, paryushan: { ...settings.paryushan, enabled: e.target.checked } })}
            className="accent-rose-500" />
          <span className="text-xs font-bold text-rose-300">Paryushan Parva (8 days — strictly no vegetables, festive satvik menu)</span>
        </label>
        {settings.paryushan?.enabled && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-[11px] text-neutral-400">Start (Day 1)
              <input type="date" value={settings.paryushan.start} data-testid="paryushan-start"
                onChange={e => saveSettings({ ...settings, paryushan: { ...settings.paryushan, start: e.target.value } })}
                className="ml-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" />
            </label>
            <label className="text-[11px] text-neutral-400">End (Samvatsari)
              <input type="date" value={settings.paryushan.end} data-testid="paryushan-end"
                onChange={e => saveSettings({ ...settings, paryushan: { ...settings.paryushan, end: e.target.value } })}
                className="ml-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" />
            </label>
          </div>
        )}
        <p className="text-[10px] text-neutral-600">During Paryushan the AI Chef plans farsan breakfasts, dal/besan mains and millet rotis — like your Paryushan menu sheet. Kids' dabba follows too.</p>
      </div>

      <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-white">Tithi calendar (auto-calculated)</p>
          <div className="flex items-center gap-2">
            <button onClick={() => nav(-1)} data-testid="jain-month-prev" className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-neutral-300 hover:text-white"><CaretLeft size={12} /></button>
            <span className="text-xs font-semibold text-neutral-300 min-w-[120px] text-center" data-testid="jain-month-label">{monthName}</span>
            <button onClick={() => nav(1)} data-testid="jain-month-next" className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-neutral-300 hover:text-white"><CaretRight size={12} /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => <div key={d} className="text-center text-[9px] font-bold text-neutral-600 uppercase">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDow }).map((_, i) => <div key={`e${i}`} />)}
          {days.map(day => (
            <button key={day.date} onClick={() => cycleDay(day)} data-testid={`jain-day-${day.date}`}
              title="Click to correct: Pancham → Atham → Chaudas → None → Auto"
              className={`rounded-lg p-1 min-h-[46px] text-left transition-colors border ${day.paryushan ? 'bg-rose-500/10 border-rose-500/25' : day.chaturmas ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-white/[0.02] border-white/5'} hover:border-white/25`}>
              <span className="text-[10px] text-neutral-400">{parseInt(day.date.slice(-2), 10)}</span>
              {day.label && (
                <span className={`block mt-0.5 px-1 py-px rounded text-[8px] font-bold uppercase border text-center ${LABEL_STYLES[day.label]} ${day.followed ? '' : 'opacity-40'} ${day.overridden ? 'ring-1 ring-white/40' : ''}`}>
                  {day.label}
                </span>
              )}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-neutral-600 mt-2">Click any day to correct it (matches your Panchang if it ever differs). Ring = manually corrected · faded = tithi shown but not followed · green tint = Chaturmas · rose tint = Paryushan.</p>
      </div>
    </div>
  );
}
